<template>
  <div>
    <b-sidebar
      id="add-new-transaction-sidebar"
      :visible="isSideBarActive"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      shadow
      backdrop
      no-header
      right
      :no-close-on-backdrop="submitBtnLoading"
      :no-close-on-esc="submitBtnLoading"
      @change="(val) => $emit('update:isSideBarActive', val)"
    >
      <template #default="{ hide }">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ transactionType === 'add' ? 'Add Transaction' : 'Deduct Transaction' }}
          </h5>

          <feather-icon
            v-if="!submitBtnLoading"
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />

        </div>
        <validation-observer
          ref="transactionFormObserver"
        >
          <b-form
            autocomplete="off"
            class="p-2"
            @submit.prevent="submitTransaction"
          >

            <b-form-group
              v-if="transactionTypes"
              label="Transaction Type"
              label-for="transaction-type"
            >
              <v-select
                id="transaction-type"
                v-model="transaction.transactionTypeId"
                label="label"
                required
                :clearable="false"
                :options="transactionTypes"
                :reduce="transaction => transaction.id"
              />
              <small
                v-if="!transactionTypeSelected"
                class="text-danger"
              >The Transaction Type field is required</small>
            </b-form-group>
            <validation-provider
              v-if="transactionType === 'add'"
              #default="validationContext"
              name="Credit Amount"
              rules="required"
            >
              <b-form-group
                label="Credit Amount"
                label-for="credit-amount"
              >
                <b-form-input
                  id="credit-amount"
                  v-model.number="transaction.creditAmount"
                  type="number"
                  step="any"
                  @input="positiveValidation"
                />
                <small class="text-danger">{{ validationContext.errors[0] }}</small>
                <small
                  v-if="!isPositiveField"
                  class="text-danger"
                >Kindly enter a value bigger than 0</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              v-else
              #default="validationContext"
              name="Debit Amount"
              rules="required"
            >
              <b-form-group
                label="Debit Amount"
                label-for="debit-amount"
              >
                <b-form-input
                  id="debit-amount"
                  v-model.number="transaction.debitAmount"
                  type="number"
                  step="any"
                  @input="positiveValidation"
                />
                <small class="text-danger">{{ validationContext.errors[0] }}</small>
                <small
                  v-if="!isPositiveField"
                  class="text-danger"
                >Kindly enter a value bigger than 0</small>
              </b-form-group>
            </validation-provider>
            <validation-provider
              #default="validationContext"
              name="Remark Text"
              rules="required"
            >
              <b-form-group
                label="Remark Text"
                label-for="remark-text"
              >
                <b-form-input
                  id="remark-text"
                  v-model="transaction.remarkText"
                />
                <small class="text-danger">{{ validationContext.errors[0] }}</small>
              </b-form-group>
            </validation-provider>
            <b-button
              variant="primary"
              type="submit"
              :disabled="submitBtnLoading"
            >
              <b-spinner
                v-if="submitBtnLoading"
                small
              />
              <span v-else>Submit</span>
            </b-button>
          </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BFormGroup, BForm, BButton, BFormInput, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import {
  computed, reactive, ref, watch,
} from '@vue/composition-api'
import vSelect from 'vue-select'
import store from '@/store'
// import { errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  props: {
    isSideBarActive: {
      type: Boolean,
      default: false,
    },
    transactionType: {
      type: String,
      default: 'add',
    },
  },
  setup(props, ctx) {
    const toast = useToast()
    const transactionTypes = computed(() => {
      let types = store.getters['transactionTypes/getTransactionTypes']
      if (types) {
        if (props.transactionType === 'add') {
          types = types.filter(type => type.visible_credit_list === true)
        } else {
          types = types.filter(type => type.visible_debit_list === true)
        }
      }
      return types
    })
    const walletId = computed(() => store.getters['driverView/getWalletId'])
    const user = computed(() => store.getters['auth/getLoggedUser'])
    const transactionTypeSelected = ref(true)
    const transactionFormObserver = ref(null)
    const transactionState = () => ({
      transactionTypeId: null,
      walletId: walletId.value,
      creditAmount: 0,
      debitAmount: 0,
      remarkText: '',
      addedBy: user.value.email,
    })
    const transaction = reactive(transactionState())

    const isPositiveField = ref(true)
    const positiveValidation = val => {
      if (val.startsWith('-')) {
        isPositiveField.value = false
      } else {
        isPositiveField.value = true
      }
    }

    const submitBtnLoading = ref(false)
    const submitTransaction = async () => {
      // if (ctx.root.$can('write', 'max-wallet-transactions')) {
      //   if (props.transactionType === 'add' && transaction.creditAmount > 3) {
      //     errorNotification('Maximum value allowed is 3')
      //     return
      //   }
      //   if (props.transactionType !== 'add' && transaction.debitAmount > 3) {
      //     errorNotification('Maximum value allowed is 3')
      //     return
      //   }
      // }
      if (!transaction.transactionTypeId) {
        transactionTypeSelected.value = false
        return
      }
      if (!isPositiveField.value) {
        return
      }
      const isValid = await transactionFormObserver.value.validate()
      if (!isValid) return
      submitBtnLoading.value = true
      store.dispatch('driverView/addDriverTransaction', {
        transaction,
        id: ctx.root.$route.query.externalId,
      })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Transaction Saved Successfully',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
          ctx.emit('update:isSideBarActive', false)
          ctx.emit('refetch-driver-wallet', false)
          ctx.emit('refetch-driver-inactivations', false)
        })
        .catch(err => console.log(err))
        .finally(() => {
          submitBtnLoading.value = false
        })
    }

    watch(() => props.isSideBarActive, () => {
      Object.assign(transaction, transactionState())
      transactionFormObserver.value.reset()
      transactionTypeSelected.value = true
      isPositiveField.value = true
    })

    return {
      transactionTypes,
      transaction,
      submitTransaction,
      submitBtnLoading,
      transactionFormObserver,
      required,
      transactionTypeSelected,
      isPositiveField,
      positiveValidation,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
